import { ReactNode } from 'react';

import {
  FILTER_OPTION_NO_VALUE,
  StaticAutocompleteFilterConfig,
} from '@/components/data-table/filters-v2';
import { CustomAttribute, CustomAttributeType } from '@/modules/common/types/custom-attribute';
import { CustomAttributeFilterRequest } from '@/modules/common/types/dto/request/custom-attribute-request';

export const CUSTOM_ATTR_FILTER_PREFIX = 'customAttr_';

const selectableAttributes = (attributes: CustomAttribute[]) =>
  attributes
    .filter(
      attr =>
        attr.type === CustomAttributeType.Select || attr.type === CustomAttributeType.MultiSelect
    )
    .filter(attr => attr.options && attr.options.length > 0);

/**
 * Optional section grouping for the generated filter configs. When `group` is set,
 * the configs render under that section header in the v2 dropdown — provided they
 * are placed consecutively with the other configs sharing the same group (the
 * dropdown buckets consecutive configs by group). `groupIcon` is taken from the
 * first config of a section, so it is only meaningful when these configs lead a group.
 */
export interface CustomAttributeFilterGroupOptions {
  group?: string;
  groupIcon?: ReactNode;
  /**
   * When set, prepends a "no value for this attribute" option (with the
   * FILTER_OPTION_NO_VALUE sentinel) to every generated filter, labelled with
   * this text. Maps to a null entry in `values` on the API filter.
   */
  noValueOptionLabel?: string;
}

/**
 * Builds v2 filter configs (for DataTableFiltersV2 / useTableFilters).
 */
export const buildCustomAttributeFilterConfigsV2 = (
  attributes: CustomAttribute[],
  startOrder: number = 10,
  groupOptions?: CustomAttributeFilterGroupOptions
): StaticAutocompleteFilterConfig[] => {
  return selectableAttributes(attributes).map(
    (attr, index): StaticAutocompleteFilterConfig => ({
      type: 'autocomplete',
      name: `${CUSTOM_ATTR_FILTER_PREFIX}${attr.id}`,
      label: attr.name,
      options: [
        ...(groupOptions?.noValueOptionLabel
          ? [{ value: FILTER_OPTION_NO_VALUE, label: groupOptions.noValueOptionLabel }]
          : []),
        ...attr.options!.map(opt => ({
          value: opt.value,
          label: opt.value,
        })),
      ],
      order: startOrder + index,
      ...(groupOptions?.group ? { group: groupOptions.group } : {}),
      ...(groupOptions?.groupIcon ? { groupIcon: groupOptions.groupIcon } : {}),
    })
  );
};

/**
 * Extracts custom attribute filters from filter values.
 * Works with any filter values object — the attribute ID is parsed from the key pattern `customAttr_{id}`.
 * No need to pass the full attributes list.
 */
export const mapCustomAttributeFilters = (
  filterValues: Record<string, string | string[]>
): CustomAttributeFilterRequest[] | undefined => {
  const result: CustomAttributeFilterRequest[] = [];

  for (const [key, value] of Object.entries(filterValues)) {
    if (!key.startsWith(CUSTOM_ATTR_FILTER_PREFIX) || !value) continue;

    const attributeId = Number(key.slice(CUSTOM_ATTR_FILTER_PREFIX.length));
    if (isNaN(attributeId)) continue;

    const allValues = Array.isArray(value) ? value : [value];
    // The UI sentinel means "member has no value for this attribute" — the wire
    // format carries it as a null entry inside values (IS NULL match on the backend)
    const values: Array<string | null> = allValues.map(v =>
      v === FILTER_OPTION_NO_VALUE ? null : v
    );
    if (values.length > 0) {
      result.push({ attributeId, values });
    }
  }

  return result.length > 0 ? result : undefined;
};
